/**
 * INV-04 — Perpetual weighted-average (moving-average) item costing.
 *
 * Owner decisions (MOVING_AVERAGE_COSTING_PLAN.md, 7 Jul 2026):
 *  1. One cost per item for the whole company (not per branch / location).
 *  2. Oversold stock: the sale uses the last known average (item-card purchase
 *     price if the item never had one) and is corrected when stock comes in.
 *  3. Already-posted normal sales are never re-priced.
 *  4. A later receipt back-fills the cost of earlier oversold sales.
 *
 * HOW IT WORKS
 * Every item keeps a running "pool" per financial year on its own record:
 *     costState.<financialYearId> = { q, v, a, pq, init, t }
 *        q  = quantity on hand (base unit)          v = stock value
 *        a  = last known average (v / q while q > 0)
 *        pq = quantity sold while out of stock (waiting for a receipt)
 * q and v are always the SUM of the item's active stock rows of that year
 * (stock in adds its value, stock out takes its value away), so the pool can
 * always be rebuilt from the rows and matches the inventory value in the
 * books. It is built automatically the first time an item is touched, and by
 * Super Admin → Data Repair.
 *
 *  • Stock IN (purchase, import, opening, add stock, returns, production
 *    output, transfers in) adds its own cost.
 *  • Stock OUT on sale-type documents (Sale Invoice, Non-Tax Invoice, Delivery
 *    Challan, POS, Reduce Stock) is priced by THIS engine at the current
 *    average — the form's cost is ignored — so the stock row and the Cost of
 *    Goods Sold entry always carry the same, correct cost. Editing such a
 *    document keeps the cost it was first saved with.
 *  • Units sold while the item was out of stock are marked as waiting; when
 *    stock arrives the server re-prices them at that receipt's cost (stock
 *    row, document line, Cost of Goods entry and the pool move together).
 *  • Every other stock OUT keeps the cost its own screen decides. Production
 *    (Material Issue, Production Receipt, Production Order, BOM) is NOT
 *    re-priced — its cost logic is left exactly as it was.
 *  • The pool changes with increments in the SAME save as the document, so two
 *    devices saving at once never overwrite each other.
 *
 * The item's `averageCost` / `currentStock` fields stay the quick-read
 * snapshot every screen already uses (POS, stock count, BOM, item list).
 *
 * The replay rules here are mirrored in functions/costing.js — keep them equal.
 */
import { collection, doc, getDoc, getDocs, query, where, increment, runTransaction, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { ACTIVE_STOCK_STATUSES, activeFyId } from './stockStatus';
import { recordReads } from './readTelemetry';

const ACTIVE = new Set(ACTIVE_STOCK_STATUSES);

// Stock-out documents priced by the engine at the current average.
export const AVG_COST_OUT_TYPES = new Set(['SI', 'NSI', 'DC', 'PS', 'RS']);
// Of those, the ones whose oversold cost is back-filled when stock arrives
// (they own their Cost of Goods / adjustment entry).
export const PENDING_COST_TYPES = new Set(['SI', 'NSI', 'PS', 'RS']);
// Production documents — their cost logic is never touched by this engine.
export const PRODUCTION_TYPES = new Set(['MIP', 'PRC', 'PRO', 'BOM']);

const EPS = 1e-9;
const num = (v) => {
  // Numbers saved as text ("12,791,310") are read as numbers, not as 0.
  const n = typeof v === 'string' ? Number(v.replace(/[,\s]/g, '')) : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const r2 = (n) => Math.round(num(n) * 100) / 100;
const r6 = (n) => Math.round(num(n) * 1e6) / 1e6;

export const isActiveStockRow = (r) =>
  !!r && !!r.itemId && ACTIVE.has(r.status) && (r.direction === 'in' || r.direction === 'out');

// A financial-year id usable inside a field path.
export const validFyKey = (fy) =>
  (typeof fy === 'string' && fy.length > 0 && !/[.~*/[\]`]/.test(fy)) ? fy : null;

// Value of a stock row (rows saved before INV-03 may lack totalCost).
export const rowValue = (r) =>
  (r.totalCost !== undefined && r.totalCost !== null) ? num(r.totalCost) : num(r.quantity) * num(r.unitCost);

// Quantity of a sale row still waiting for its cost.
export const pendingQtyOf = (r) =>
  (r && r.direction === 'out' && r.costPending === true) ? Math.max(0, num(r.costPendingQty)) : 0;

// An old sale saved with no cost at all (before INV-04). Counted at the
// average of that moment (or the next receipt's cost) until Data Repair
// stores that cost on the row itself (rows it has checked are marked).
const isLegacyZeroSale = (r) =>
  r.direction === 'out' && PENDING_COST_TYPES.has(r.tranTypeCode) && r.costPending !== true
  && !r.costRepricedAt && num(r.quantity) > 0 && !(rowValue(r) > 0);

export const poolOf = (item, fy) => {
  const p = item && item.costState && fy ? item.costState[fy] : null;
  if (!p || p.init !== true) return null;
  return { q: num(p.q), v: num(p.v), a: num(p.a), pq: Math.max(0, num(p.pq)) };
};

// Base-unit cost for stock leaving a pool.
export const unitCostFromPool = (pool, item) => {
  if (pool && pool.q > EPS && pool.v > 0) return pool.v / pool.q;
  if (pool && pool.a > 0) return pool.a;
  return Math.max(0, num(item && item.purchasePrice));
};

const tsNum = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return 0; } }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
};
const rowOrder = (x, y) =>
  String(x.docDate || '').localeCompare(String(y.docDate || ''))
  || (tsNum(x.createdAt) - tsNum(y.createdAt))
  || (num(x.lineNumber) - num(y.lineNumber));

/**
 * Rebuild one item's pool for one year from its stock rows (in date order).
 * `rows` = that item's rows already limited to the year (rows with no year
 * count in every year). q / v are the sum of the rows; old no-cost sales are
 * counted at the average of their moment (see isLegacyZeroSale).
 */
export const replayItemRows = (rows, item) => {
  const sorted = (rows || []).filter(isActiveStockRow).slice().sort(rowOrder);
  let q = 0; let v = 0; let a = 0; let pq = 0;
  let waitQ = 0; // old no-cost sales with no cost known yet
  for (const r of sorted) {
    const qty = num(r.quantity);
    if (qty === 0) continue;
    const val = rowValue(r);
    if (r.direction === 'in') {
      q += qty; v += val;
      if (waitQ > EPS && qty > 0 && val > 0) {
        const s = Math.min(waitQ, qty);
        v -= s * (val / qty);
        waitQ -= s;
      }
    } else if (isLegacyZeroSale(r)) {
      const known = (q > EPS && v > 0) ? v / q : a;
      q -= qty;
      if (known > 0) v -= qty * known; else waitQ += qty;
    } else {
      q -= qty; v -= val;
      pq += pendingQtyOf(r);
    }
    if (q > EPS && v > 0) a = v / q;
  }
  if (waitQ > EPS) {
    const pp = Math.max(0, num(item && item.purchasePrice));
    if (pp > 0) v -= waitQ * pp;
    if (q > EPS && v > 0) a = v / q;
  }
  return { q: r6(q), v: r6(v), a: r6(a), pq: r6(pq) };
};

// Rows of one item in one year → fresh pool (reads that item's stock rows once).
export const buildPoolFromRows = async (companyId, item, fy) => {
  const snap = await getDocs(query(
    collection(db, 'companies', companyId, 'stockFlow'),
    where('itemId', '==', item.id),
    where('status', 'in', ACTIVE_STOCK_STATUSES)
  ));
  recordReads(companyId, snap.size, 'cost-pool');
  const rows = snap.docs.map((d) => d.data()).filter((r) => !r.financialYearId || r.financialYearId === fy);
  return replayItemRows(rows, item);
};

/**
 * Per item + year: what the added and removed rows change.
 * The add… / rem… fields hold the signed effect (+ for in, − for out) of each
 * set; inQ / inV / remInQ are the stock-in parts only.
 */
export const rowDeltas = (added = [], removed = [], defaultFy = null) => {
  const out = new Map();
  const touch = (r) => {
    const fy = validFyKey(r.financialYearId || defaultFy);
    if (!fy) return null;
    const k = `${r.itemId}|${fy}`;
    let d = out.get(k);
    if (!d) {
      d = { key: k, itemId: r.itemId, fy, addQ: 0, addV: 0, remQ: 0, remV: 0, inQ: 0, inV: 0, remInQ: 0, addPq: 0, remPq: 0, inTx: null };
      out.set(k, d);
    }
    return d;
  };
  added.forEach((r) => {
    if (!isActiveStockRow(r)) return;
    const d = touch(r); if (!d) return;
    const qty = num(r.quantity); const val = rowValue(r);
    if (r.direction === 'in') {
      d.addQ += qty; d.addV += val; d.inQ += qty; d.inV += val;
      if (!d.inTx && r.transactionId) d.inTx = r.transactionId;
    } else { d.addQ -= qty; d.addV -= val; }
    d.addPq += pendingQtyOf(r);
  });
  removed.forEach((r) => {
    if (!isActiveStockRow(r)) return;
    const d = touch(r); if (!d) return;
    const qty = num(r.quantity); const val = rowValue(r);
    if (r.direction === 'in') { d.remQ += qty; d.remV += val; d.remInQ += qty; }
    else { d.remQ -= qty; d.remV -= val; }
    d.remPq += pendingQtyOf(r);
  });
  return [...out.values()];
};

// Pool after a change. `pool` = pool before (it still includes the removed rows).
export const applyDelta = (pool, d) => {
  const q1 = pool.q - d.remQ + d.addQ;
  const v1 = pool.v - d.remV + d.addV;
  const a1 = (q1 > EPS && v1 > 0) ? v1 / q1 : pool.a;
  const pq1 = Math.max(0, pool.pq - d.remPq + d.addPq);
  return { q: q1, v: v1, a: a1, pq: pq1 };
};

export const snapshotAverage = (p) => ((p.q > EPS && p.v > 0) ? p.v / p.q : (p.a > 0 ? p.a : 0));

/**
 * Firestore update for one item: increments only, so two devices can post
 * the same item at the same moment without losing either save.
 */
export const poolUpdateData = (item, fy, before, after, d) => {
  const now = new Date().toISOString();
  const P = `costState.${fy}`;
  const data = {
    [`${P}.q`]: increment(r6(d.addQ - d.remQ)),
    [`${P}.v`]: increment(r6(d.addV - d.remV)),
    [`${P}.pq`]: increment(r6(after.pq - before.pq)),
    [`${P}.a`]: r6(after.a),
    [`${P}.t`]: now,
  };
  // Snapshot fields on the item card: kept for the year being worked in.
  const active = activeFyId();
  if (!active || fy === active || !item.costFy || item.costFy === fy) {
    const avg = r6(snapshotAverage(after));
    const sameYear = item.costFy === fy;
    data.averageCost = avg;
    data.costFy = fy;
    data.costUpdatedAt = now;
    data.currentStock = sameYear ? increment(r6(d.addQ - d.remQ)) : r6(after.q);
    // Other devices refresh their item list only when the AVERAGE moved.
    const old = num(item.averageCost);
    if (!sameYear || Math.abs(avg - old) > Math.max(1e-6, Math.abs(old) * 1e-6)) data.updatedAt = now;
  }
  return data;
};

/**
 * First use of an item in a year: build its pool from its stock rows and save
 * it in a small transaction of its own — if another device built it a moment
 * earlier, that one is used instead (so two first saves never overwrite each
 * other). Returns { pool, item } with the item as now stored.
 */
const initPoolOnce = async (companyId, item, fy) => {
  const built = await buildPoolFromRows(companyId, item, fy);
  const ref = doc(db, 'companies', companyId, 'inventoryItems', item.id);
  return runTransaction(db, async (t) => {
    const snap = await t.get(ref);
    if (!snap.exists()) return { pool: built, item };
    const cur = { id: item.id, ...snap.data() };
    const existing = poolOf(cur, fy);
    if (existing) return { pool: existing, item: cur };
    const now = new Date().toISOString();
    const data = { [`costState.${fy}`]: { ...built, init: true, t: now } };
    const next = { ...cur, costState: { ...(cur.costState || {}), [fy]: { ...built, init: true } } };
    const active = activeFyId();
    if (!active || fy === active || !cur.costFy || cur.costFy === fy) {
      data.costFy = fy;
      data.currentStock = r6(built.q);
      data.averageCost = r6(snapshotAverage(built));
      data.costUpdatedAt = now;
      data.updatedAt = now;
      Object.assign(next, { costFy: fy, currentStock: data.currentStock, averageCost: data.averageCost });
    }
    t.update(ref, data);
    return { pool: built, item: next };
  });
};

/**
 * Read the items (fresh) and their pools for the given years, building any
 * pool that does not exist yet from the item's stock rows.
 */
export const loadCostContext = async (companyId, itemIds, fys) => {
  const items = {};
  const pools = {};
  const years = [...new Set((fys || []).map(validFyKey).filter(Boolean))];
  await Promise.all([...new Set(itemIds)].filter(Boolean).map(async (id) => {
    try {
      const snap = await getDoc(doc(db, 'companies', companyId, 'inventoryItems', id));
      recordReads(companyId, 1, 'cost-pool');
      if (!snap.exists()) return;
      let item = { id, ...snap.data() };
      if (item.itemType === 'service') return;
      for (const fy of years) {
        let p = poolOf(item, fy);
        if (!p) {
          // eslint-disable-next-line no-await-in-loop
          const res = await initPoolOnce(companyId, item, fy);
          p = res.pool;
          item = res.item;
        }
        pools[`${id}|${fy}`] = p;
      }
      items[id] = item;
    } catch (e) {
      console.warn('[cost] could not load item cost state:', id, e && e.message);
    }
  }));
  return { items, pools };
};

const lineBaseQty = (l) => {
  if (l.baseQuantity !== undefined && l.baseQuantity !== null) return num(l.baseQuantity);
  return num(l.quantity) * (num(l.conversion) || 1);
};

/**
 * Cost per base unit each item went out at in this document's previous
 * version (an edit). Only real costs are kept — a sale that was saved with no
 * cost, or still waiting for one, is priced again.
 */
const previousOutCosts = (removedRows, tranTypeCode, fy) => {
  const acc = {};
  (removedRows || []).forEach((r) => {
    if (!isActiveStockRow(r) || r.direction !== 'out' || r.tranTypeCode !== tranTypeCode) return;
    if (r.financialYearId && r.financialYearId !== fy) return;
    const a = acc[r.itemId] || (acc[r.itemId] = { q: 0, v: 0, bad: false });
    a.q += num(r.quantity);
    a.v += rowValue(r);
    if (pendingQtyOf(r) > 0 || !(rowValue(r) > 0)) a.bad = true;
  });
  const out = {};
  Object.entries(acc).forEach(([id, a]) => { if (!a.bad && a.q > EPS && a.v > 0) out[id] = a.v / a.q; });
  return out;
};

/**
 * Before a document's rows are built: price its stock-out lines at the
 * current average (sale-type documents only) and give Add Stock lines a total
 * cost so their accounting entry posts.
 *
 * Returns { lines, finish(writer, addedRows) } — call finish once the rows are
 * known, with a writer that has update(ref, data) (and optionally
 * afterCommit(fn)); it adds the item updates to that same save.
 */
export const prepareStockCosting = async (companyId, {
  tranTypeCode, lines = [], financialYearId, removedRows = [], postsStock = true,
}) => {
  const fy = validFyKey(financialYearId);
  const stockLines = (lines || []).filter((l) => l && l.itemId && l.itemType !== 'service');
  const itemIds = [...stockLines.map((l) => l.itemId), ...(removedRows || []).map((r) => r.itemId)];
  const years = [fy, ...(removedRows || []).map((r) => r.financialYearId)];
  // Cost marks from an earlier save, or copied from another document (e.g. a
  // return made from an invoice), are worked out again below.
  let outLines = (lines || []).map((l) => {
    if (!l || (l.costSource === undefined && l.costPendingQty === undefined)) return l;
    const { costSource, costPendingQty, ...rest } = l;
    return rest;
  });
  // Add Stock: the line total always follows quantity × cost (also on edits).
  if (tranTypeCode === 'AS') {
    outLines = outLines.map((l) => ((l && l.itemId)
      ? { ...l, totalCost: r2(num(l.quantity) * num(l.unitCost)) }
      : l));
  }
  const noop = { lines: outLines, ctx: null, finish: () => {} };
  if (!fy || itemIds.length === 0) return noop;

  let ctx;
  try {
    ctx = await loadCostContext(companyId, itemIds, years);
  } catch (e) {
    console.warn('[cost] costing skipped:', e && e.message);
    return noop;
  }

  // Effect of THIS document's previous rows (an edit) per item/year, so the
  // new lines are priced as if the old version was not there.
  const prev = new Map();
  rowDeltas([], removedRows, fy).forEach((d) => prev.set(d.key, d));

  if (postsStock && AVG_COST_OUT_TYPES.has(tranTypeCode)) {
    const keepCost = previousOutCosts(removedRows, tranTypeCode, fy);
    const left = {}; // stock still available per item while pricing this document
    outLines = outLines.map((l) => {
      if (!l || !l.itemId || l.itemType === 'service') return l;
      const item = ctx.items[l.itemId];
      const k = `${l.itemId}|${fy}`;
      const pool = ctx.pools[k];
      if (!item || !pool) return l;
      const p = prev.get(k);
      const base = p ? { q: pool.q - p.remQ, v: pool.v - p.remV, a: pool.a } : pool;
      const c = keepCost[l.itemId] > 0 ? keepCost[l.itemId] : unitCostFromPool(base, item);
      if (left[l.itemId] === undefined) left[l.itemId] = Math.max(0, base.q);
      const bq = lineBaseQty(l);
      const conv = num(l.conversion) || 1;
      const oversold = Math.max(0, bq - left[l.itemId]);
      left[l.itemId] = Math.max(0, left[l.itemId] - bq);
      const next = {
        ...l,
        baseUnitCost: r6(c),
        unitCost: r6(c * conv),
        totalCost: r2(bq * c),
        costSource: 'average',
      };
      // No cost known at all (e.g. stock entered without a rate and no
      // purchase price) → the whole line waits for the next receipt's cost.
      const waiting = c > 0 ? oversold : bq;
      if (waiting > 1e-6 && PENDING_COST_TYPES.has(tranTypeCode)) next.costPendingQty = r6(waiting);
      else delete next.costPendingQty;
      return next;
    });
  }
  // A sale return with no cost (e.g. typed without its invoice) comes back in
  // at the current average — never at the selling price.
  if (postsStock && (tranTypeCode === 'SR' || tranTypeCode === 'NSR')) {
    outLines = outLines.map((l) => {
      if (!l || !l.itemId || l.itemType === 'service' || num(l.unitCost) > 0) return l;
      const item = ctx.items[l.itemId];
      const pool = ctx.pools[`${l.itemId}|${fy}`];
      if (!item || !pool) return l;
      const c = unitCostFromPool(pool, item);
      const conv = num(l.conversion) || 1;
      return { ...l, baseUnitCost: r6(c), unitCost: r6(c * conv), totalCost: r2(lineBaseQty(l) * c), costSource: 'average' };
    });
  }

  const finish = (writer, addedRows = []) => applyStockCosting(companyId, ctx, writer, addedRows, removedRows, fy);
  return { lines: outLines, ctx, finish };
};

/**
 * Add the item pool / snapshot updates for rows written (added) and rows
 * deleted or deactivated (removed) to the writer's save. `ctx` comes from
 * loadCostContext (or prepareStockCosting).
 */
export const applyStockCosting = (companyId, ctx, writer, addedRows = [], removedRows = [], defaultFy = null) => {
  if (!ctx || !writer) return [];
  const settle = [];
  const deltas = rowDeltas(addedRows, removedRows, defaultFy);
  deltas.forEach((d) => {
    const item = ctx.items[d.itemId];
    const pool = ctx.pools[d.key];
    if (!item || !pool) return;
    if (Math.abs(d.addQ - d.remQ) < EPS && Math.abs(d.addV - d.remV) < 1e-6
        && Math.abs(d.addPq - d.remPq) < EPS) return; // nothing changes
    const after = applyDelta(pool, d);
    const data = poolUpdateData(item, d.fy, pool, after, d);
    writer.update(doc(db, 'companies', companyId, 'inventoryItems', d.itemId), data);
    // Keep the in-memory copy current in case the same context is used again.
    ctx.pools[d.key] = { q: after.q, v: after.v, a: after.a, pq: after.pq };
    item.costFy = d.fy;
    if (data.averageCost !== undefined) item.averageCost = data.averageCost;
    // Sales waiting for a cost + NEW stock arriving with a cost → back-fill
    // them at that receipt's cost (a transfer between locations adds nothing).
    const newIn = Math.min(d.inQ - d.remInQ, d.addQ - d.remQ);
    if (pool.pq > EPS && newIn > EPS && d.inV > 0 && d.inTx) {
      settle.push({ itemId: d.itemId, fy: d.fy, transactionId: d.inTx, qty: r6(newIn) });
    }
  });
  if (settle.length && typeof writer.afterCommit === 'function') {
    // Fire and forget — the save is finished; the server does the rest.
    writer.afterCommit(() => { settlePendingCosts(companyId, settle); });
  }
  return settle;
};

/**
 * Ask the server to re-price sales of these items that went out while out of
 * stock, at the cost of the stock that just arrived (their stock rows,
 * document lines, Cost of Goods entries and the pool). Best effort — anything
 * missed is picked up by the next receipt or by Data Repair.
 */
export const settlePendingCosts = async (companyId, list) => {
  if (!companyId || !Array.isArray(list) || !list.length) return;
  try {
    const fn = httpsCallable(functions, 'settlePendingCosts');
    await fn({ companyId, items: list.slice(0, 50).map((x) => ({ itemId: x.itemId, fy: x.fy, transactionId: x.transactionId, qty: r6(x.qty) })) });
  } catch (e) {
    console.warn('[cost] oversold sales will be re-priced on the next receipt / Data Repair:', e && e.message);
  }
};

/**
 * Standalone helper for code that writes or deletes stock rows outside
 * createTransaction (stock transfers, production orders, POS, deletes):
 * reads the items, then adds their pool updates to `writer`.
 */
export const costRowsChange = async (companyId, writer, { added = [], removed = [], financialYearId = null } = {}) => {
  const fy = validFyKey(financialYearId);
  // Only items whose quantity / value / waiting quantity really changes
  // (e.g. a status move between two active statuses changes nothing → no reads).
  const moving = rowDeltas(added, removed, fy).filter((d) =>
    Math.abs(d.addQ - d.remQ) > EPS || Math.abs(d.addV - d.remV) > 1e-6 || Math.abs(d.addPq - d.remPq) > EPS);
  if (!moving.length) return [];
  const ctx = await loadCostContext(companyId, moving.map((d) => d.itemId), moving.map((d) => d.fy));
  return applyStockCosting(companyId, ctx, writer, added, removed, fy);
};

/**
 * Blind increments for the POS (no reads — the POS must stay instant). The
 * POS prices its lines from the item's average already on the device. If an
 * item's pool has not been built yet these increments are harmless: the pool
 * is built from the rows (which include this sale) and replaces them.
 */
export const posPoolIncrements = (companyId, rows, financialYearId) => {
  const updates = [];
  rowDeltas(rows, [], financialYearId).forEach((d) => {
    const dq = r6(d.addQ);
    const dv = r6(d.addV);
    const data = {
      [`costState.${d.fy}.q`]: increment(dq),
      [`costState.${d.fy}.v`]: increment(dv),
      currentStock: increment(dq),
    };
    if (d.addPq > 0) data[`costState.${d.fy}.pq`] = increment(r6(d.addPq));
    updates.push({ ref: doc(db, 'companies', companyId, 'inventoryItems', d.itemId), data, itemId: d.itemId, fy: d.fy });
  });
  return updates;
};

/**
 * Item updates that did not fit in the sale's own save (a very large bill):
 * saved right after it. Best effort — Data Repair rebuilds anything missed.
 */
export const commitPoolUpdates = async (updates) => {
  try {
    for (let i = 0; i < (updates || []).length; i += 450) {
      const b = writeBatch(db);
      updates.slice(i, i + 450).forEach((u) => b.update(u.ref, u.data));
      // eslint-disable-next-line no-await-in-loop
      await b.commit();
    }
  } catch (e) {
    console.warn('[cost] item averages not updated for this bill (Data Repair will rebuild them):', e && e.message);
  }
};

/**
 * POS: a sale line with no cost at all (the item never had an average or a
 * purchase price on this device) waits for the next receipt's cost.
 */
export const markZeroCostPosRows = (rows) => {
  (rows || []).forEach((r) => {
    if (r && r.tranTypeCode === 'PS' && r.direction === 'out' && r.itemId
        && !(num(r.unitCost) > 0) && num(r.quantity) > 0) {
      r.costPendingQty = r6(num(r.quantity));
      r.costPending = true;
    }
  });
  return rows;
};
